/**
 * Hydrates all server-side rendered (SSRed) instances in the DOM of components
 * included in {@link componentClasses}.
 *
 * Expects each SSRed component instance in the DOM to be prefixed with
 * `<script type="application/json" class="svelte-config">...</script>`
 * and postfixed with `<!-- svelte-component-end -->`. (This is how Baker
 * outputs {% ssrSvelte %} tags in Nunjucks.)
 *
 * @param {{[componentName: string]: import("svelte").SvelteComponent}} componentClasses Collection
 *     of
 *     [client-side](https://svelte.dev/docs#run-time-client-side-component-api)
 *     Svelte components. All components that have been SSRed into the DOM that
 *     you want to hydrate must be represented in this object.
 */
export const hydrate = function hydrateAllComponentsOnPage(componentClasses) {
    // Each SSRed component instance has a startMarker right before it.
    const startMarkers = document.querySelectorAll(
        'script[type="application/json"].svelte-config'
    );

    startMarkers.forEach((startMarker) => {
        const config = JSON.parse(startMarker.textContent);
        const { componentName, props, contextArray } = config;
        const Component = componentClasses[componentName];

        // If componentClasses doesn't contain the specified component, fail
        // gracefully and silently.
        // This feature can be used purposefully for performance: in some cases
        // you might not want to load all the Svelte components in a single JS
        // bundle (for example, if one Svelte component relies on a huge amount
        // of data that gets compiled into it). Example scenario:
        //
        // * Use `hydrate({SiteHeader, SiteFooter})` in app.js and load app.js
        //   on every page of the app.
        // * Use `hydrate({DataHeavyComponent})` in detail.js, which is only
        //   loaded on detail pages.
        //
        // On the detail pages, each separate call to `hydrate` will hydrate
        // the appropriate components.
        if (!Component) return;

        // Insert an empty div after the startMarker (before the SSRed content)
        const tempParent = document.createElement("div");
        startMarker.after(tempParent);

        // For each top-level element that comprises the SSRed content, move it
        // into the temporary <div>. Stop when we see
        // <!-- svelte-component-end --> (which appears after SSRed content).
        let currentElem = tempParent.nextSibling;
        let nextElem;
        while (
            !(
                currentElem.nodeType === Node.COMMENT_NODE &&
                currentElem.nodeValue === "svelte-component-end"
            )
        ) {
            nextElem = currentElem.nextSibling;
            tempParent.appendChild(currentElem);
            currentElem = nextElem;
        }

        // Hydrate the div that contains all our SSR content

        try {
            // https://svelte.dev/docs#run-time-client-side-component-api
            /* eslint-disable-next-line no-new -- Svelte API uses `new` to
            instantiate component, but we have no need to keep a reference to
            the instance at the moment. May change in the future. */
            new Component({
                hydrate: true,
                target: tempParent,
                props,
                context: new Map(contextArray),
            });
        } catch {
            console.error(`Error instantiating ${componentName}.`);
        }

        // Remove the parent div (but keep the children)
        tempParent.replaceWith(...tempParent.children);
        // Remove the start marker (to make sure we don't try to accidentally
        // hydrate it again if there are multiple `hydrate` calls on one page)
        startMarker.remove();
    });
};
